import { CommonSettingsStateModel, DEFAULT_COMMON_SETTINGS_STATE_MODEL } from './common-settings.model';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
  getLoadedResults,
  isEmpty,
  Loadable,
  LoadableHelper,
  LoadableStatus,
  removeDuplicatesByField,
  ResetState,
  setLoadableInState,
  UsState,
  UsStateAbbreviation,
} from '@aiwyn/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, pairwise, switchMap, take, tap } from 'rxjs/operators';
import {
  AutoPay,
  ClientApiService,
  ClientCurrencies,
  Currency,
  DEFAULT_CURRENCY,
  PaymentPlanApiService,
  PaymentsApiService,
  RawClientCurrency,
} from '@aiwyn/api';
import { AuthService, GuestUser } from '@aiwyn/auth';
import { GetAutoPayClients, GetClientCurrencies, GetUsStates } from './common-settings.actions';
import { partition } from 'lodash';

export const COMMON_SETTINGS_SLICE_NAME = 'commonSettings';

@Injectable({
  providedIn: 'root',
})
@State<CommonSettingsStateModel>({
  name: COMMON_SETTINGS_SLICE_NAME,
  defaults: DEFAULT_COMMON_SETTINGS_STATE_MODEL,
})
export class CommonSettingsState {
  @Selector()
  static getAutoPayClientsLoadable(state: CommonSettingsStateModel): Loadable<AutoPay[]> {
    return state?.autoPayClients;
  }

  @Selector([CommonSettingsState.getAutoPayClientsLoadable])
  static getAutoPayClientsStatus(autoPayClientsLoadable: Loadable<AutoPay[]>): LoadableStatus {
    return autoPayClientsLoadable?.status;
  }

  @Selector([CommonSettingsState.getAutoPayClientsLoadable])
  static getAutoPayClients(autoPayClientsLoadable: Loadable<AutoPay[]>): AutoPay[] {
    return autoPayClientsLoadable?.data ?? [];
  }

  @Selector()
  static getUsStatesLoadable(state: CommonSettingsStateModel): Loadable<UsState[]> {
    return state?.usStates;
  }

  @Selector([CommonSettingsState.getUsStatesLoadable])
  static getUsStatesStatus(usStatesLoadable: Loadable<UsState[]>): LoadableStatus {
    return usStatesLoadable?.status;
  }

  @Selector([CommonSettingsState.getUsStatesLoadable])
  static getUsStates(usStatesLoadable: Loadable<UsState[]>): UsState[] {
    return usStatesLoadable?.data ?? [];
  }

  @Selector([CommonSettingsState.getUsStates])
  static getOptOutOfProcessingFeeUsStates(usStates: UsState[]): UsState[] {
    return usStates?.filter(usState => usState.optOutOfProcessingFee);
  }

  @Selector([CommonSettingsState.getUsStates])
  static getRestrictedUsStates(usStates: UsState[]): UsState[] {
    return usStates?.filter(usState => usState.disableCreditCard);
  }

  static isRestrictedState(usStateAbbreviation: UsStateAbbreviation): (usStates: UsState[]) => boolean {
    return createSelector(
      [CommonSettingsState.getRestrictedUsStates],
      disableCreditCardUsStates => disableCreditCardUsStates?.some(usState => usState.stateAbbreviation === usStateAbbreviation),
    );
  }

  static getUsState(usStateAbbreviation: UsStateAbbreviation): (usStates: UsState[]) => UsState {
    return createSelector(
      [CommonSettingsState.getUsStates],
      usStates => usStates?.find(usState => usState.stateAbbreviation === usStateAbbreviation),
    );
  }

  @Selector()
  static getClientCurrencies(state: CommonSettingsStateModel): ClientCurrencies {
    return state?.clientCurrencies ?? {};
  }

  @Selector([CommonSettingsState.getClientCurrencies])
  static getClientCurrenciesStatus(clientCurrencies: ClientCurrencies): LoadableStatus {
    return LoadableHelper.coalesceStatus(Object.values(clientCurrencies).map(({ status }) => status));
  }

  @Selector([CommonSettingsState.getClientCurrencies])
  static getClientCurrenciesData(clientCurrencies: ClientCurrencies): Currency[] {
    const data = Object.values(clientCurrencies).map(({ data }) => data).filter(Boolean);
    return removeDuplicatesByField(data, 'code');
  }

  @Selector([CommonSettingsState.getClientCurrenciesData])
  static doMultipleClientCurrenciesExist(clientCurrencies: Currency[]): boolean {
    return isEmpty(clientCurrencies) ? undefined : clientCurrencies.length > 1;
  }

  static getClientCurrencyLoadable(clientId: number): (clientCurrencies: ClientCurrencies) => Loadable<Currency> {
    return createSelector(
      [CommonSettingsState.getClientCurrencies],
      clientCurrencies => clientCurrencies[clientId],
    );
  }

  static getClientCurrencyStatus(clientId: number): (clientCurrencyLoadable: Loadable<Currency>) => LoadableStatus {
    return createSelector(
      [CommonSettingsState.getClientCurrencyLoadable(clientId)],
      clientCurrencyLoadable => clientCurrencyLoadable?.status,
    );
  }

  static getClientCurrency(clientId: number): (clientCurrencyLoadable: Loadable<Currency>) => Currency {
    return createSelector(
      [CommonSettingsState.getClientCurrencyLoadable(clientId)],
      clientCurrencyLoadable => clientCurrencyLoadable?.data,
    );
  }

  constructor(
    private readonly store: Store,
    private readonly paymentPlanApiService: PaymentPlanApiService,
    private readonly paymentsApiService: PaymentsApiService,
    private readonly clientApiService: ClientApiService,
    private readonly authService: AuthService,
  ) {
    this.authService.getUser(true).pipe(
      map(Boolean),
      pairwise(),
      filter(([wasLoggedIn, isLoggedIn]) => !wasLoggedIn && isLoggedIn),
      tap(_ => this.store.dispatch(new GetUsStates())),
    ).subscribe();
  }

  @Action([GetAutoPayClients])
  getAutoPayClients(
    { patchState }: StateContext<CommonSettingsStateModel>,
    { forceRefresh }: GetAutoPayClients,
  ): Observable<AutoPay[] | undefined> | void {
    const autopayClientsStatus = this.store.selectSnapshot(CommonSettingsState.getAutoPayClientsStatus);
    if (!(LoadableHelper.shouldRequest(autopayClientsStatus) || forceRefresh)) {
      return getLoadedResults(this.store.select(CommonSettingsState.getAutoPayClientsLoadable), true).pipe(
        take(1),
      );
    }

    patchState({ autoPayClients: { status: LoadableStatus.LOADING } });

    // TODO :: consider refactoring this into the application user strategies
    return this.authService.getUser().pipe(
      switchMap(user =>
        user instanceof GuestUser
          ? of(undefined)
          : this.paymentPlanApiService.getAutoPayClients(user.getClientIdList()),
      ),
      setLoadableInState(patchState, 'autoPayClients'),
      take(1),
    );
  }

  /**
   * Make sure the configs have loaded before stashing them in the state.
   * @param patchState
   */
  @Action([GetUsStates])
  getUsStates({ patchState }: StateContext<CommonSettingsStateModel>): Observable<UsState[]> {
    patchState({ usStates: { status: LoadableStatus.LOADING } });

    return this.paymentsApiService.getUsStates().pipe(
      setLoadableInState(patchState, 'usStates'),
      take(1),
    );
  }

  @Action([GetClientCurrencies])
  getClientCurrencies(
    { patchState }: StateContext<CommonSettingsStateModel>,
    { clientIds }: GetClientCurrencies,
  ): Observable<Currency[] | RawClientCurrency[]> {
    const existingClientCurrencies = this.store.selectSnapshot(CommonSettingsState.getClientCurrencies);
    const [loadedClientIds, notLoadedClientIds] = partition(
      clientIds,
      clientId => existingClientCurrencies[clientId] && !LoadableHelper.shouldRequest(existingClientCurrencies[clientId].status),
    );

    if (isEmpty(notLoadedClientIds)) {
      return combineLatest(
        loadedClientIds.map(clientId => getLoadedResults(this.store.select(CommonSettingsState.getClientCurrencyLoadable(clientId)), true)),
      ).pipe(
        take(1),
      );
    }

    const updatedClientCurrencies: ClientCurrencies = notLoadedClientIds.reduce(
      (acc, clientId) => ({ ...acc, [clientId]: { status: LoadableStatus.LOADING } }),
      {},
    );

    patchState({ clientCurrencies: { ...existingClientCurrencies, ...updatedClientCurrencies } });

    return this.clientApiService.getClientCurrencies(notLoadedClientIds).pipe(
      tap({
        next: (clientCurrencies: RawClientCurrency[]) => {
          const marshaledClientCurrencies = this._marshalClientCurrencies(clientCurrencies, LoadableStatus.LOADED);
          patchState({ clientCurrencies: { ...existingClientCurrencies, ...marshaledClientCurrencies } });
        },
        error: _ => {
          const marshaledClientCurrencies = this._marshalClientCurrencies(
            notLoadedClientIds.map(clientId => ({ id: clientId })) as RawClientCurrency[],
            LoadableStatus.ERROR,
          );
          patchState({ clientCurrencies: { ...existingClientCurrencies, ...marshaledClientCurrencies } });
        },
      }),
    );
  }

  private _marshalClientCurrencies(
    clientCurrencies: RawClientCurrency[],
    status: LoadableStatus.LOADED | LoadableStatus.ERROR,
  ): ClientCurrencies {
    return clientCurrencies.reduce(
      (acc, clientCurrency) => ({
        ...acc,
        [clientCurrency.id]: { status, data: clientCurrency.arTrans?.[0]?.currency ?? DEFAULT_CURRENCY },
      }),
      {},
    );
  }

  @Action([ResetState])
  resetState({ setState }: StateContext<CommonSettingsStateModel>): void {
    setState(DEFAULT_COMMON_SETTINGS_STATE_MODEL);
  }
}
